import { RefObject, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  churchOrgTypesForChooser,
  ClientOrg,
  HouseholdOriginSource,
  OrgBase,
  OrgType,
  schoolOrgTypes,
} from '../../../../common/src/model.ts';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import { matchesSearch } from '../../../../common/src/search.ts';
import * as server_api from '../../../../common/src/server_api.ts';
import { capitalize, naturalStringCompare } from '../../../../common/src/util.ts';
import { LocalStorageKey } from '../../data/client_local_storage.ts';
import { useCurrentOrg } from '../../data/use_current_org.tsx';
import { useHousehold } from '../../data/use_household.tsx';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { useAllOrgs } from '../../data/use_orgs.tsx';
import { SearchContext, SearchControl } from '../../util/search_control.tsx';
import { Spinner } from '../../util/spinner.tsx';
import { useDebounce } from '../../util/use_debounce.tsx';
import { useEffectOnce } from '../../util/use_effect_once.ts';
import { Button, LeftAlignedButton } from './button.tsx';
import { ButtonContent, goToNextOnboardingStep, setOnboardingHouseholdSurveyResponse } from './common.tsx';
import { BesideSmallBackButton, Frame } from './frame.tsx';

export interface Props {
  onSettingChanged?(): void;
  onBack?(): void;
  orgTypes?: OrgType[];
}

const minSearchLength = 3;

export const OrgChoice = ({ onSettingChanged, onBack, orgTypes }: Props) => {
  const { origin } = useLocalStorage(LocalStorageKey.OnboardingHouseholdSurveyResponses)[0];
  orgTypes = orgTypes ?? getOrgTypesFromOnboardingSurveyOrigin(origin) ?? churchOrgTypesForChooser;
  const { orgs: allOrgs, isLoading: orgsLoading } = useAllOrgs();
  const allOrgsOfType = allOrgs.filter(({ type }) => !orgTypes || orgTypes.includes(type));
  const { currentOrg, householdOrgs, setHouseholdOrg, clearHouseholdOrgTypes } = useCurrentOrg();
  const { searchString, setSearchString, searchInputRef } = use(SearchContext);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const { household } = useHousehold();
  const [onboardingOrg, setOnboardingOrg] = useLocalStorage(LocalStorageKey.OnboardingOrg);
  const [selectContent, setSelectContent] = useState(false);
  const isOrgSelected = useMemo(() => {
    const org = currentOrg ?? onboardingOrg;
    return !!(org && orgTypes.includes(org.type));
  }, [currentOrg, onboardingOrg, orgTypes]);
  const isInSettings = !!onSettingChanged;
  const navigate = useNavigate();

  useEffect(() => {
    // if the default search string did not turn up any results, clear it (see comment in getDefaultSearchString)
    // Ideally, this would be a useEffectOnce, but the org list populates asynchronously, so we wait for it to populate
    if (allOrgsOfType.length > 0 && !selectContent) {
      const defaultSearchString = getDefaultSearchString(householdOrgs, orgTypes);
      setSearchString(filterOrgs(allOrgsOfType, defaultSearchString).length === 0 ? '' : defaultSearchString);
      // SearchControl's .select() effect fires when selectContent transitions to true; we want it
      // to run *after* setSearchString above so the populated string is what gets selected. The two
      // setStates are batched into a single commit, preserving that ordering.
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setSelectContent(true);
    }
    // eslint-disable-next-line
  }, [allOrgsOfType]);

  useEffectOnce(() => {
    return () => setSearchString('');
  });

  // Suppress auto-focus in onboarding path so keyboard doesn't pop up before user reads the prompt
  useEffect(() => {
    if (!isInSettings) {
      searchInputRef.current?.blur();
    }
  }, [isInSettings, searchInputRef, selectContent]);

  const onContinue = useCallback(async () => {
    if (onSettingChanged) {
      onSettingChanged();
      return;
    }
    setOnboardingHouseholdSurveyResponse({ choseOrganization: true });
    await goToNextOnboardingStep(navigate, { from: OnboardingPages.OrgChoice, action: 'continue' });
  }, [onSettingChanged, navigate]);

  const onSetOrg = useCallback(
    async (selectedOrg?: OrgBase) => {
      if (household) {
        if (!selectedOrg || selectedOrg.id === currentOrg?.id) {
          if (!orgTypes) {
            return;
          }
          void clearHouseholdOrgTypes(orgTypes);
        } else {
          void setHouseholdOrg(selectedOrg);
        }
      } else {
        setOnboardingOrg(selectedOrg?.id === onboardingOrg?.id ? undefined : selectedOrg);
      }
    },
    [clearHouseholdOrgTypes, currentOrg?.id, household, onboardingOrg?.id, orgTypes, setHouseholdOrg, setOnboardingOrg],
  );

  const onSearchCancel = useCallback(() => {
    scrollableRef.current?.scrollTo(0, 0);
    if (isInSettings) {
      searchInputRef.current?.focus();
    }
  }, [isInSettings, searchInputRef]);

  const noun = orgTypes?.includes(OrgType.School) && !orgTypes.includes(OrgType.Church) ? 'school' : 'church';
  const prompt = `What city is your ${noun} in?`;

  const onOrgNotFound = useCallback(async () => {
    await onSetOrg(undefined);
    if (onSettingChanged) {
      onSettingChanged();
    } else {
      setOnboardingHouseholdSurveyResponse({ choseOrganization: false });
      await goToNextOnboardingStep(navigate, { from: OnboardingPages.OrgChoice, action: 'orgNotListed' });
    }
  }, [onSetOrg, onSettingChanged, navigate]);

  // live search with a short debounce; the minimum character count discourages browsing for a random
  // church - the customer must know their church's name (or city) to find it
  const debouncedSearchString = useDebounce(searchString, 300);
  const searchStringForResults = isInSettings ? searchString : debouncedSearchString;
  const showResults = isInSettings || debouncedSearchString.trim().length >= minSearchLength;
  const isSmallBackButton = Boolean(onSettingChanged);

  return (
    <Frame
      onboardingPage={OnboardingPages.OrgChoice}
      mainContent={
        <>
          {isSmallBackButton ? (
            <BesideSmallBackButton>
              <Message $widthInChars={22}>{prompt}</Message>
            </BesideSmallBackButton>
          ) : (
            <Message $widthInChars={22} style={{ paddingTop: '20px' }}>
              {prompt}
            </Message>
          )}
          <SubHeaderWithOpaqueBackground>
            <SearchControl
              placeholder={`City, State OR ${capitalize(noun)} Name`}
              onCancel={onSearchCancel}
              selectContent={selectContent}
            />
          </SubHeaderWithOpaqueBackground>
          {showResults ? (
            orgsLoading ? (
              <SpinnerWrapper>
                <Spinner />
              </SpinnerWrapper>
            ) : (
              <OrgList
                allOrgsOfType={allOrgsOfType}
                searchString={searchStringForResults}
                onSetOrg={onSetOrg}
                scrollableRef={scrollableRef}
              />
            )
          ) : (
            <EmptyOrgListMessage noun={noun} />
          )}
        </>
      }
      footerContent={
        <>
          {isOrgSelected && <Button onClick={onContinue}>CONTINUE</Button>}
          <Button onClick={onOrgNotFound} notPrimary={true}>
            My {noun} isn&apos;t in the app
          </Button>
        </>
      }
      onEnterKeypress={isOrgSelected ? onContinue : undefined}
      isSmallBackButton={isSmallBackButton}
      onBack={onBack}
      alignTop={true}
    />
  );
};

function EmptyOrgListMessage({ noun }: { noun: string }) {
  return (
    <EmptyOrgListMessageWrapper>
      <EmptyOrgListMessageText>Type a city or the name of your {noun}</EmptyOrgListMessageText>
    </EmptyOrgListMessageWrapper>
  );
}

const EmptyOrgListMessageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpinnerWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyOrgListMessageText = styled.div`
  width: 20ch;
  text-align: center;
`;

function OrgList({
  allOrgsOfType,
  searchString,
  onSetOrg,
  scrollableRef,
}: {
  allOrgsOfType: OrgBase[];
  searchString: string;
  onSetOrg: (selectedOrg?: OrgBase) => Promise<void>;
  scrollableRef: RefObject<HTMLDivElement | null>;
}) {
  const { household } = useHousehold();
  const [onboardingOrg] = useLocalStorage(LocalStorageKey.OnboardingOrg);

  const orgsFiltered = useMemo(() => filterOrgs(allOrgsOfType, searchString), [allOrgsOfType, searchString]);

  const sortedOrgs = useMemo(
    () => [
      ...orgsFiltered.sort((lhs, rhs) => {
        const lhsName = lhs.name.startsWith('The ') ? lhs.name.slice(4) : lhs.name;
        const rhsName = rhs.name.startsWith('The ') ? rhs.name.slice(4) : rhs.name;
        return naturalStringCompare(lhsName, rhsName);
      }),
    ],
    [orgsFiltered],
  );

  const orgsRendered = useMemo(
    () =>
      sortedOrgs.map((org) => {
        const isSelected =
          (household && household.orgIds.includes(org.id)) || (!household && org.id === onboardingOrg?.id);
        const key = org.name + org.location;
        return <OrgButton key={key} org={org} isSelected={isSelected} onSetOrg={onSetOrg} />;
      }),
    [household, onSetOrg, onboardingOrg?.id, sortedOrgs],
  );

  return <ButtonContent ref={scrollableRef}>{orgsRendered}</ButtonContent>;
}

function OrgButton({
  org,
  isSelected,
  onSetOrg,
}: {
  org: OrgBase;
  isSelected: boolean;
  onSetOrg: (org: OrgBase) => Promise<void>;
}) {
  const { name, location } = org;

  const onClick = useCallback(async () => {
    await onSetOrg(org);
  }, [onSetOrg, org]);

  return (
    <LeftAlignedButton selected={isSelected} key={name + location} onClick={onClick} notPrimary={true}>
      <OrgWrapper>
        <OrgName>{name}</OrgName>
        <OrgLocation>{location}</OrgLocation>
      </OrgWrapper>
    </LeftAlignedButton>
  );
}

const OrgWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis; // TODO(hewitt): determine why ellipsis is not working
  text-align: left;
  padding: 5px 15px;
`;

const OrgName = styled.div``;

const OrgLocation = styled.div`
  font-family: Jost-Regular, sans-serif;
  color: var(--color-text-light);
`;

const SubHeaderWithOpaqueBackground = styled.div`
  background-color: var(--color-background);
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 10px;
  padding-bottom: 7px;
  text-align: center;
`;

export const Message = styled.div<{ $widthInChars: number }>`
  font-size: 1.2em;
  width: ${(props) => props.$widthInChars}ch;
`;

function getOrgTypesFromOnboardingSurveyOrigin(origin?: HouseholdOriginSource): OrgType[] | undefined {
  switch (origin) {
    case HouseholdOriginSource.Church:
      return churchOrgTypesForChooser;
    case HouseholdOriginSource.School:
      return schoolOrgTypes;
    default:
      return;
  }
}

function filterOrgs(allOrgsOfType: OrgBase[], searchString: string): OrgBase[] {
  return allOrgsOfType.filter((org) => matchesSearch({ search: searchString, target: server_api.stringFromOrg(org) }));
}

function getDefaultSearchString(householdOrgs: ClientOrg[], orgTypes: OrgType[] | undefined): string {
  // Prefer the location of current household org with matching type so that the currently selected org is visible.
  // Fall back on the location of another org type so that if a church is selected, then show schools in the same town.
  // However, if either makes the search list empty, then return an empty search string.
  return householdOrgs.find(({ type }) => orgTypes?.includes(type))?.location ?? householdOrgs?.[0]?.location ?? '';
}
